import { useLatest } from 'ahooks';
import { useCallback, useRef, useState } from 'react';
import { Editor } from '@meitu/saas-editor-core';
import { useMountEffect } from '@meitu/saas-editor-core/es/framework';

type IUsePressToDeleteProps = {
    editor: Editor;
    handleAfterCopyOrDel: () => void;
    isPickStage: boolean;
    isFocusInNavigation: boolean;
    cb?: () => void;
};
// 添加删除页面的快捷键处理
export const usePressToDelete = ({
    editor,
    handleAfterCopyOrDel,
    isPickStage,
    isFocusInNavigation,
    cb,
}: IUsePressToDeleteProps) => {
    const [selectedIndexes, setSelectedIndexes] = useState<number[]>(editor.services.frame.getFrameSelection());

    const selectedIndexesRef = useLatest(selectedIndexes);
    const isPickStageRef = useLatest(isPickStage);
    const isFocusInNavigationRef = useLatest(isFocusInNavigation);
    const cbRef = useLatest(cb);
    const handleAfterCopyOrDelRef = useLatest(handleAfterCopyOrDel);

    useMountEffect(() => {
        const handler = editor.services.frame.onFrameSelectionChange((indexes) => {
            setSelectedIndexes(indexes);
        });
        return () => editor.services.events.off(handler);
    });

    useMountEffect(() => {
        const handler = editor.services.hotkey.onDelete(() => {
            const selectedIndexes = selectedIndexesRef.current || [];
            if (!isPickStageRef.current || selectedIndexes.length < 1) {
                return;
            }
            // 焦点不在导航栏, 不响应删除事件
            if (!isFocusInNavigationRef.current) {
                return;
            }
            cbRef.current?.();
            editor.services.stage.batchDelete(selectedIndexes);
            handleAfterCopyOrDelRef.current?.();
            return true;
        }, 1000);

        return () => {
            editor.off(handler);
        };
    });
};

type IMultiSelectState = Set<number>;

// 用于多选功能
export const useMultiSelectState = (editor: Editor) => {
    const multiSelectState = useRef<IMultiSelectState>(new Set());

    const handleMultiSelect = useCallback((index: number, event: React.MouseEvent) => {
        const activeIndex = editor.services.frame.getActiveIndex();
        const state = multiSelectState.current;

        // Command/Ctrl + 点击实现非连续选择
        if (event.metaKey || event.ctrlKey) {
            if (index === activeIndex) return;
            if (state.has(index)) {
                state.delete(index);
            } else {
                state.add(index);
            }
        }
        // Shift + 点击实现连续选择
        else if (event.shiftKey) {
            if (state.has(index)) return;
            state.add(index);

            const selectedIndexes = Array.from(state);
            const start = Math.min(...selectedIndexes);
            const end = Math.max(...selectedIndexes);

            state.clear();
            for (let i = start; i <= end; i++) {
                state.add(i);
            }
        }
        // 普通点击
        else {
            state.clear();
            state.add(index);
            // 普通点击时, 激活舞台为点击的舞台，多选时不改变当前激活舞台
            editor.services.frame.setActiveIndex(index);
            editor.services.frame.scrollToActiveFrame();
        }

        editor.services.frame.setFrameSelection(Array.from(state).sort((a, b) => a - b));
    }, []);

    const clearSelection = useCallback(() => {
        multiSelectState.current.clear();
        editor.services.frame.setFrameSelection([]);
    }, []);

    const setSelection = useCallback((selectedIndexes: number[]) => {
        multiSelectState.current.clear();
        if (selectedIndexes.length) {
            selectedIndexes.forEach((index) => multiSelectState.current.add(index));
        }
        editor.services.frame.setFrameSelection(Array.from(multiSelectState.current).sort((a, b) => a - b));
    }, []);

    // 处理复制或删除后的选中逻辑
    const handleAfterCopyOrDel = useCallback((isPaste: boolean = false) => {
        setTimeout(() => {
            const activeIndex = editor.services.frame.getActiveIndex();
            let indexes = [activeIndex];
            if (isPaste) {
                const stageSelection = editor.services.frame.getFrameSelection();
                indexes = Array.from({ length: stageSelection.length }, (_, i) => activeIndex + i);
            }
            const state = multiSelectState.current;
            state.clear();
            indexes.forEach((index) => state.add(index));
            editor.services.frame.setFrameSelection([...state].sort((a, b) => a - b));
            editor.services.frame.scrollToActiveFrame();
        }, 0);
    }, []);

    return {
        handleMultiSelect,
        handleAfterCopyOrDel,
        setSelection,
        clearSelection,
    };
};
